import { useEffect, useState, useCallback } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { FileText, ExternalLink, Tag } from "lucide-react";
import { money, BACKEND } from "./shared";
import { InvoiceEditor } from "./InvoiceEditor";

const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

export function Invoice({ data, role, reference, reload }) {
  const b = data.booking;
  const canManage = role === "office_staff" || role === "admin" || role === "senior_admin";
  const [spec, setSpec] = useState([]);
  const [disc, setDisc] = useState({ type: "pct", value: "", reason: "" });

  const loadSpec = useCallback(() => {
    api.get(`/admin/booking/${reference}/job`).then(r => setSpec(r.data.spec || [])).catch(() => {});
  }, [reference]);
  useEffect(() => { loadSpec(); }, [loadSpec]);

  const total = Number(b.total_inc_vat || 0);
  const deposit = Number(b.deposit_amount ?? b.deposit_total ?? 0);
  const expressFee = Number(b.express_fee || 0);
  const customItems = b.custom_line_items || [];
  const customSum = r2(customItems.reduce((s, i) => s + (Number(i.amount) || 0), 0));
  const discountLines = [];
  (b.pricing_breakdown || []).forEach((it) => { const a = Number(it.amount || 0); if (a < 0) discountLines.push([it.label || "Discount", r2(-a)]); });
  if (b.discount_code && Number(b.discount_code.amount) > 0) discountLines.push([`Discount code ${b.discount_code.code} (${b.discount_code.label})`, r2(b.discount_code.amount)]);
  if (b.manual_discount && Number(b.manual_discount.amount) > 0) discountLines.push([b.manual_discount.label + (b.manual_discount.reason ? ` (${b.manual_discount.reason})` : ""), r2(b.manual_discount.amount)]);
  const discSum = r2(discountLines.reduce((s, [, a]) => s + a, 0));
  const preTotal = r2(total + discSum);
  const baseLine = r2(preTotal - customSum - expressFee);
  const balance = Math.max(0, r2(total - (data.paid_to_date || 0)));
  const routeLabel = b.route === "planning" ? "Planning-permission route" : "Permitted Development route";
  const pdfUrl = `${BACKEND}/api/admin/booking/${reference}/invoice.pdf`;

  const applyDiscount = async () => {
    if (!disc.value || parseFloat(disc.value) <= 0) { toast.error("Enter a discount value"); return; }
    try {
      const r = await api.put(`/admin/booking/${reference}/discount`, { type: disc.type, value: parseFloat(disc.value), reason: disc.reason });
      toast.success(`Discount applied — new total ${money(r.data.total_inc_vat)}`);
      setDisc({ type: "pct", value: "", reason: "" }); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply discount"); }
  };
  const removeDiscount = async () => {
    try { await api.put(`/admin/booking/${reference}/discount`, { type: "none" }); toast.success("Discount removed"); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove discount"); }
  };

  return (
    <div data-testid="jobfile-invoice" className="space-y-6">
      {/* Full invoice preview */}
      <div className="border border-[#E5E7EB] bg-white" data-testid="invoice-preview">
        <div className="flex items-start justify-between gap-3 border-b border-[#E5E7EB] p-5">
          <div>
            <div className="text-lg font-black tracking-tight text-[#12263A]">The Extension Works</div>
            <div className="text-xs text-[#6B7280]">Making home extensions simple</div>
          </div>
          <div className="text-right">
            <div className="label-eyebrow">Invoice</div>
            <div className="font-mono font-bold text-[#12263A]">INV-{b.reference}</div>
            {b.manual_discount && <div className="text-[10px] font-bold uppercase tracking-wider text-[#1F6B3B] mt-1">{b.manual_discount.label} applied</div>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 text-sm">
          <div>
            <div className="label-eyebrow mb-1">Billed to</div>
            <div className="font-bold text-[#12263A]">{b.name || "—"}</div>
            {b.address && <div className="text-[#6B7280]">{b.address}</div>}
            {b.postcode && <div className="text-[#6B7280]">{b.postcode}</div>}
            {b.email && <div className="text-[#6B7280]">{b.email}</div>}
          </div>
          <div className="sm:text-right">
            <div className="label-eyebrow mb-1">Details</div>
            <div className="text-[#6B7280]">Reference: <span className="font-mono text-[#12263A]">{b.reference}</span></div>
            <div className="text-[#6B7280]">Route: {routeLabel}</div>
            <div className="text-[#6B7280]">Start date: {b.start_date || "TBC"}</div>
          </div>
        </div>

        <div className="px-5 pb-2">
          <table className="w-full text-sm" data-testid="invoice-preview-table">
            <thead>
              <tr className="bg-[#12263A] text-white text-left text-xs uppercase tracking-wide">
                <th className="px-3 py-2">Description</th>
                <th className="px-3 py-2 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-[#E5E7EB]">
                <td className="px-3 py-2">Single-storey rear extension — {routeLabel}</td>
                <td className="px-3 py-2 text-right tabular-nums">{money(baseLine)}</td>
              </tr>
              {customItems.map((it, i) => (
                <tr key={it.id || i} data-testid={`invoice-preview-line-${i}`} className="border-b border-[#E5E7EB]">
                  <td className="px-3 py-2">
                    {it.description}
                    {Number(it.qty || 1) !== 1 && it.unit_price != null && <span className="text-[#9CA3AF]"> ({Number(it.qty)} × {money(it.unit_price)})</span>}
                    {it.discount_status === "applied" && Number(it.discount_amount || 0) > 0 && <span className="text-[#1F6B3B]"> · {money(it.discount_amount)} discount</span>}
                    {it.discount_status === "pending" && <span className="text-[#9A6B12]"> · discount awaiting authorisation</span>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(it.amount)}</td>
                </tr>
              ))}
              {expressFee > 0 && (
                <tr className="border-b border-[#E5E7EB]">
                  <td className="px-3 py-2">Express service fee</td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(expressFee)}</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              {discountLines.length > 0 && (
                <>
                  <tr className="border-t border-[#E5E7EB] font-bold">
                    <td className="px-3 py-1.5">Subtotal (incl. VAT)</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{money(preTotal)}</td>
                  </tr>
                  {discountLines.map(([label, amt], i) => (
                    <tr key={`${label}-${i}`} data-testid={`invoice-preview-discount-${i}`} className="text-[#1F6B3B]">
                      <td className="px-3 py-1">{label}</td>
                      <td className="px-3 py-1 text-right tabular-nums">−{money(amt)}</td>
                    </tr>
                  ))}
                </>
              )}
              <tr className="border-t-2 border-[#12263A] font-black">
                <td className="px-3 py-2">Contract total (incl. VAT)</td>
                <td className="px-3 py-2 text-right tabular-nums" data-testid="invoice-preview-total">{money(total)}</td>
              </tr>
              <tr className="text-[#6B7280]">
                <td className="px-3 py-1">Deposit (10%)</td>
                <td className="px-3 py-1 text-right tabular-nums">{money(deposit + expressFee)}</td>
              </tr>
              <tr className="text-[#6B7280]">
                <td className="px-3 py-1">Paid to date</td>
                <td className="px-3 py-1 text-right tabular-nums">{money(data.paid_to_date)}</td>
              </tr>
              <tr className="text-[#12263A] font-bold">
                <td className="px-3 py-1">Balance outstanding</td>
                <td className="px-3 py-1 text-right tabular-nums" data-testid="invoice-preview-balance">{money(balance)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {spec.length > 0 && (
          <div className="px-5 py-4 border-t border-[#E5E7EB]">
            <div className="label-eyebrow mb-2">Your extension specification</div>
            <dl className="divide-y divide-[#F0EEE9]">
              {spec.map(([label, value], i) => (
                <div key={`${label}-${i}`} className="py-1.5 grid grid-cols-[130px_1fr] gap-3 text-sm">
                  <dt className="font-bold text-[#12263A]">{label}</dt>
                  <dd className="text-[#6B7280]">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        <div className="flex items-center justify-between gap-2 border-t border-[#E5E7EB] px-5 py-3 flex-wrap">
          <p className="text-[11px] text-[#9CA3AF] max-w-md">Payment schedule: 10% deposit on booking, 85% across 14 weekly payments during the build, 5% one week after completion. All prices include VAT at 20%.</p>
          <a data-testid="invoice-view-pdf" href={pdfUrl} target="_blank" rel="noreferrer" className="de-btn-secondary text-xs inline-flex items-center gap-1.5 shrink-0">
            <FileText size={13} /> View full PDF <ExternalLink size={12} />
          </a>
        </div>
      </div>

      {/* Discount */}
      {canManage && (
        <div className="border border-[#E5E7EB] bg-white p-4" data-testid="jobfile-discount">
          <div className="font-bold mb-2 flex items-center gap-2"><Tag size={16} /> Whole-invoice discount</div>
          {b.manual_discount ? (
            <div className="flex items-center justify-between gap-3 text-sm">
              <div>
                <span className="font-bold text-[#1F6B3B]" data-testid="discount-applied-label">{b.manual_discount.label}</span> applied
                {b.manual_discount.reason && <span className="text-[#6B7280]"> · {b.manual_discount.reason}</span>}
                <div className="text-xs text-[#6B7280]">Was {money(b.original_total_inc_vat)} → now {money(b.total_inc_vat)} · by {b.manual_discount.applied_by}</div>
              </div>
              <button data-testid="discount-remove" onClick={removeDiscount} className="de-btn-secondary text-xs shrink-0">Remove</button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 items-center">
              <select data-testid="discount-type" value={disc.type} onChange={e => setDisc({ ...disc, type: e.target.value })} className="border border-[#E5E7EB] px-2 py-2 text-sm bg-white">
                <option value="pct">%</option><option value="gbp">£</option>
              </select>
              <input data-testid="discount-value" type="number" value={disc.value} onChange={e => setDisc({ ...disc, value: e.target.value })} placeholder={disc.type === "pct" ? "e.g. 10" : "e.g. 500"} className="border border-[#E5E7EB] px-3 py-2 text-sm w-28" />
              <input data-testid="discount-reason" value={disc.reason} onChange={e => setDisc({ ...disc, reason: e.target.value })} placeholder="Reason (optional)" className="border border-[#E5E7EB] px-3 py-2 text-sm flex-1 min-w-[140px]" />
              <button data-testid="discount-apply" onClick={applyDiscount} className="de-btn-primary text-sm">Apply discount</button>
            </div>
          )}
        </div>
      )}

      {/* Line-item editor + add charges */}
      {canManage && <InvoiceEditor data={data} role={role} reference={reference} reload={reload} />}
    </div>
  );
}
